import React from "react";
import { useParams } from "react-router-dom";
import AppTable from "components/Shared/AppTable";
import { localizeDateTimeShort } from "utils/dateFormats";
import { useTablePagination } from "hooks";
import { Chip } from "@mui/material";
import { useLazyGetLeadStepLogQuery } from "store/services/vendorLeadApi";

const STEP_LABEL = {
    vatPan: "VAT / PAN",
    businessRegistration: "Business Registration",
    financeContactDoc: "Finance Contact Document",
    slaDoc: "SLA Document",
    pickupLocationMapping: "Pickup Location Mapping",
    destinationPreferences: "Destination Preferences",
    servicePreferences: "Service Preferences",
    portalTraining: "Portal Training",
};

const VALUE_COLOR = {
    Open: { label: "Open", color: "default" },
    "In Progress": { label: "In Progress", color: "warning" },
    Completed: { label: "Completed", color: "success" },
};

const ValueChip = ({ value }) => {
    if (!value) return <span style={{ color: "#999" }}>—</span>;
    const c = VALUE_COLOR[value] ?? { label: value, color: "default" };
    return <Chip size="small" label={c.label} color={c.color} />;
};

const VendorLeadStepLogTab = () => {
    const { id } = useParams();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [fetchStepLog, { data: logRes, isLoading, isFetching }] =
        useLazyGetLeadStepLogQuery();

    React.useEffect(() => {
        fetchStepLog({ id, page: currentPage, limit: perPage });
    }, [id, currentPage, perPage, fetchStepLog]);

    const columns = [
        {
            name: "Step",
            selector: (row) => row?.field,
            cell: (row) => (
                <div className="line-clamp-1">
                    {STEP_LABEL[row?.field] ?? row?.field ?? "N/A"}
                </div>
            ),
            minWidth: "200px",
        },
        {
            name: "Changed By",
            selector: (row) => row?.changedBy?.name,
            cell: (row) => (
                <div className="line-clamp-1">
                    {row?.changedBy?.name || "System"}
                </div>
            ),
            minWidth: "150px",
        },
        {
            name: "From",
            selector: (row) => row?.fromValue,
            cell: (row) => <ValueChip value={row?.fromValue} />,
            minWidth: "130px",
        },
        {
            name: "To",
            selector: (row) => row?.toValue,
            cell: (row) => <ValueChip value={row?.toValue} />,
            minWidth: "130px",
        },
        {
            name: "Changed At",
            selector: (row) => row?.createdAt,
            cell: (row) => (
                <div className="line-clamp-1">
                    {row?.createdAt ? localizeDateTimeShort(row.createdAt) : "N/A"}
                </div>
            ),
            minWidth: "170px",
        },
    ];

    return (
        <AppTable
            columns={columns}
            data={logRes?.data}
            progressPending={isLoading || isFetching}
            paginationTotalRows={logRes?.totalSize ?? 0}
            paginationPerPage={perPage}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
        />
    );
};

export default VendorLeadStepLogTab;